import React from "react";
import { Link } from "react-router-dom";
import { Logo, LogoLight } from "../components/Logo";
import PublicMobileMenu from "../components/PublicMobileMenu";
import {
  ArrowRight, UserPlus, BookOpen, Lock, Share2,
  ShieldCheck, Sparkles, Award, Building2, Landmark, Trophy,
  Microscope, Globe, FileSignature, Users,
} from "lucide-react";

// --- Étapes "Comment ça marche" ---
const STEPS = [
  {
    icon: UserPlus,
    title: "1. Créez votre compte",
    desc: "Inscription en 2 minutes avec votre prénom, nom et date de naissance. Forfait Découverte gratuit pour démarrer, puis Essentiel (3,99 €/mois) ou Confort (5,99 €/mois) pour débloquer toutes les rubriques.",
  },
  {
    icon: BookOpen,
    title: "2. Remplissez votre carnet",
    desc: "11 rubriques guidées (identité, famille, couple, patrimoine, dernières volontés…). Sauvegarde automatique à chaque saisie, progression visualisée, alertes intelligentes en cas d'oubli successoral.",
  },
  {
    icon: Lock,
    title: "3. Déposez vos documents",
    desc: "Coffre-fort numérique sécurisé pour vos documents importants : testament, contrat de mariage, actes notariés, polices d'assurance, factures du caveau familial…",
  },
  {
    icon: Share2,
    title: "4. Partagez à votre rythme",
    desc: "Carnet partagé entre conjoints (lecture seule mutuelle), partages ponctuels au notaire, et « Mode Héritier » qui débloque l'accès à vos proches après votre décès — sur déclaration validée.",
  },
];

// --- Synthèse en chiffres ---
const STATS = [
  { value: "11", label: "Rubriques structurées", desc: "De l'identité aux dernières volontés" },
  { value: "100 %", label: "Hébergé en France", desc: "Conforme RGPD · données chiffrées" },
  { value: "60 %", label: "des familles", desc: "découvrent le patrimoine après le décès" },
  { value: "15", label: "Experts au comité scientifique", desc: "Notaires, juristes, conseillers" },
];

// --- Timeline événements forts ---
const MILESTONES = [
  {
    date: "Novembre 2024",
    title: "Lancement officiel",
    place: "Assemblée Nationale",
    icon: Landmark,
    accent: "navy",
    highlight: true,
  },
  {
    date: "2024 — 2025",
    title: "Présentations institutionnelles",
    place: "Sénat · Mairie de Paris",
    icon: Building2,
    accent: "navy",
  },
  {
    date: "2025",
    title: "Dossier transmis",
    place: "Conseiller Outre-mer de l'Élysée",
    icon: FileSignature,
    accent: "gold",
  },
  {
    date: "2025",
    title: "Dossier soumis",
    place: "Ministre des Outre-mer",
    icon: FileSignature,
    accent: "gold",
  },
  {
    date: "2025",
    title: "Prix Innovation Outre-mer",
    place: "Reconnaissance nationale",
    icon: Award,
    accent: "gold",
    highlight: true,
  },
  {
    date: "2025",
    title: "Prix Innovation Sociale & Technologique",
    place: "Pour l'impact patrimonial des familles",
    icon: Sparkles,
    accent: "gold",
  },
  {
    date: "2025",
    title: "Entrepreneur Afro-Créole de l'année",
    place: "Distinction entrepreneuriat",
    icon: Award,
    accent: "gold",
  },
  {
    date: "2025",
    title: "Trophée Entrepreneur",
    place: "RCI Guadeloupe",
    icon: Trophy,
    accent: "gold",
  },
  {
    date: "2025",
    title: "Trophée Innovation",
    place: "RIDD Côte d'Ivoire",
    icon: Trophy,
    accent: "green",
  },
  {
    date: "2025",
    title: "Comité scientifique constitué",
    place: "15 experts : notaires, juristes, conseillers",
    icon: Microscope,
    accent: "navy",
    highlight: true,
  },
];

export default function CommentCaMarche() {
  return (
    <div style={{ backgroundColor: "var(--auprea-cream)" }} className="min-h-screen">
      {/* Top nav */}
      <nav
        className="flex items-center justify-between px-6 md:px-12 py-6 border-b"
        style={{ borderColor: "rgba(21,41,79,0.1)" }}
      >
        <Link to="/" data-testid="nav-home"><Logo size={28} /></Link>
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/comment-ca-marche"
            className="hidden md:inline-block text-sm"
            style={{ color: "var(--auprea-navy)", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}
            data-testid="nav-cmm"
          >
            Comment ça marche
          </Link>
          <Link
            to="/tarifs"
            className="hidden md:inline-block text-sm"
            style={{ color: "var(--auprea-navy)", letterSpacing: "0.06em", textTransform: "uppercase" }}
            data-testid="nav-tarifs"
          >
            Tarifs
          </Link>
          <Link to="/connexion" data-testid="nav-login">
            <button className="auprea-btn-outline hidden md:inline-block" style={{ padding: "10px 22px", minHeight: "44px" }} data-testid="btn-nav-login">
              Se connecter
            </button>
          </Link>
          {/* Menu mobile (< 768px) : hamburger + drawer */}
          <PublicMobileMenu />
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-20 pb-12" data-testid="cmm-hero">
        <div className="max-w-5xl mx-auto">
          <div style={{ color: "var(--auprea-gold)", letterSpacing: "0.2em", fontSize: 13, fontWeight: 600, textTransform: "uppercase", marginBottom: 20 }}>
            — Le service AUPREA en clair
          </div>
          <h1 style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)", lineHeight: 1.1, fontWeight: 400, marginBottom: 20 }}>
            Comment ça marche ?
          </h1>
          <div className="divider-gold" />
          <p style={{ fontSize: "1.125rem", lineHeight: 1.7, color: "var(--auprea-ink)", maxWidth: 720 }}>
            AUPREA est un carnet patrimonial numérique pensé pour les particuliers qui veulent
            anticiper sereinement leur succession. Voici comment notre outil vous accompagne, étape
            par étape, et ce qui le distingue.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="px-6 md:px-12 lg:px-24 py-12" data-testid="cmm-steps">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  data-testid={`cmm-step-${i + 1}`}
                  style={{
                    background: "#fff",
                    border: "1px solid rgba(21,41,79,0.08)",
                    padding: "28px 28px 26px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: -1,
                      left: -1,
                      width: 56,
                      height: 4,
                      background: "var(--auprea-gold)",
                    }}
                  />
                  <div className="flex items-start gap-4">
                    <div
                      style={{
                        width: 48, height: 48, flexShrink: 0,
                        background: "rgba(21,41,79,0.06)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "var(--auprea-navy)",
                      }}
                    >
                      <Icon size={22} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 22, marginBottom: 8, color: "var(--auprea-navy)" }}>{s.title}</h3>
                      <p style={{ color: "rgba(35,38,46,0.8)", lineHeight: 1.65 }}>{s.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 md:px-12 lg:px-24 py-16" style={{ background: "var(--auprea-navy)", color: "var(--auprea-cream)" }} data-testid="cmm-stats">
        <div className="max-w-6xl mx-auto">
          <div style={{ color: "var(--auprea-gold)", letterSpacing: "0.2em", fontSize: 13, fontWeight: 600, textTransform: "uppercase", marginBottom: 12 }}>
            — Le service en chiffres
          </div>
          <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", lineHeight: 1.2, marginBottom: 40, color: "var(--auprea-cream)", maxWidth: 720 }}>
            Pourquoi confier votre patrimoine à AUPREA ?
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {STATS.map((s, i) => (
              <div key={i} data-testid={`cmm-stat-${i}`}>
                <div style={{ fontFamily: "Spectral, serif", fontSize: "clamp(2.5rem, 4vw, 3.25rem)", fontWeight: 300, color: "var(--auprea-gold)", lineHeight: 1, marginBottom: 8 }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--auprea-cream)", marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 13, color: "rgba(243,239,230,0.65)", lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sécurité */}
      <section className="px-6 md:px-12 lg:px-24 py-16" data-testid="cmm-security">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div style={{ color: "var(--auprea-gold)", letterSpacing: "0.2em", fontSize: 13, fontWeight: 600, textTransform: "uppercase", marginBottom: 16 }}>
                — Confidentialité absolue
              </div>
              <h2 style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)", lineHeight: 1.2, marginBottom: 20 }}>
                Vos données restent en France, chiffrées et accessibles uniquement par vous.
              </h2>
              <ul className="space-y-4" style={{ fontSize: 16, lineHeight: 1.65 }}>
                <li className="flex gap-3"><ShieldCheck size={20} style={{ color: "var(--auprea-green)", flexShrink: 0, marginTop: 2 }} />
                  <span>Hébergement européen conforme RGPD, sauvegardes chiffrées quotidiennes</span></li>
                <li className="flex gap-3"><Lock size={20} style={{ color: "var(--auprea-green)", flexShrink: 0, marginTop: 2 }} />
                  <span>Authentification renforcée et 2FA pour les comptes administrateurs</span></li>
                <li className="flex gap-3"><Users size={20} style={{ color: "var(--auprea-green)", flexShrink: 0, marginTop: 2 }} />
                  <span>Aucun partage commercial : vos données ne sont jamais revendues</span></li>
                <li className="flex gap-3"><Globe size={20} style={{ color: "var(--auprea-green)", flexShrink: 0, marginTop: 2 }} />
                  <span>Accès « Mode Héritier » uniquement après validation par notre équipe</span></li>
              </ul>
            </div>
            <div className="hidden lg:block">
              <div style={{
                background: "#fff",
                border: "1px solid rgba(21,41,79,0.1)",
                padding: 36,
                position: "relative",
              }}>
                <div style={{ position: "absolute", top: 0, right: 0, padding: "8px 14px", background: "var(--auprea-gold)", color: "var(--auprea-navy)", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                  Conforme RGPD
                </div>
                <Sparkles size={32} color="var(--auprea-gold)" />
                <div style={{ fontFamily: "Spectral, serif", fontSize: 26, lineHeight: 1.3, marginTop: 16, color: "var(--auprea-navy)" }}>
                  « Un carnet patrimonial pensé comme une étude notariale moderne. »
                </div>
                <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginTop: 14, fontStyle: "italic" }}>
                  Notre engagement AUPREA
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reconnaissances & étapes clés */}
      <section
        className="px-6 md:px-12 lg:px-24 py-20"
        style={{ background: "linear-gradient(180deg, rgba(243,239,230,0) 0%, rgba(243,239,230,1) 30%, rgba(243,239,230,1) 100%)", backgroundColor: "#faf7f0" }}
        data-testid="cmm-milestones"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div style={{ color: "var(--auprea-gold)", letterSpacing: "0.2em", fontSize: 13, fontWeight: 600, textTransform: "uppercase", marginBottom: 16 }}>
              — Notre parcours
            </div>
            <h2 style={{ fontSize: "clamp(2rem, 3.5vw, 2.75rem)", lineHeight: 1.15, marginBottom: 16 }}>
              Reconnaissances & étapes fortes
            </h2>
            <p style={{ fontSize: 16, color: "rgba(35,38,46,0.7)", maxWidth: 640, margin: "0 auto", lineHeight: 1.6 }}>
              Depuis son lancement en novembre 2024, AUPREA a été présenté aux plus hautes
              institutions et distingué par plusieurs prix d&apos;innovation.
            </p>
          </div>

          <div className="relative" data-testid="cmm-timeline">
            {/* Ligne verticale centrale (desktop) */}
            <div
              className="hidden md:block"
              style={{
                position: "absolute",
                left: "50%",
                top: 0, bottom: 0,
                width: 2,
                background: "linear-gradient(180deg, var(--auprea-gold) 0%, rgba(202,168,106,0.2) 100%)",
                transform: "translateX(-50%)",
              }}
            />

            <div className="space-y-6 md:space-y-10">
              {MILESTONES.map((m, idx) => {
                const Icon = m.icon;
                const isLeft = idx % 2 === 0;
                const accentColor =
                  m.accent === "navy" ? "var(--auprea-navy)" :
                  m.accent === "green" ? "var(--auprea-green)" :
                  "var(--auprea-gold)";
                return (
                  <div
                    key={idx}
                    data-testid={`cmm-milestone-${idx}`}
                    className={`relative md:grid md:grid-cols-2 md:gap-12 items-center`}
                  >
                    {/* Card */}
                    <div className={`md:col-start-${isLeft ? "1" : "2"} md:col-end-${isLeft ? "2" : "3"} ${isLeft ? "md:text-right" : ""}`}>
                      <div
                        style={{
                          background: "#fff",
                          border: `1px solid ${m.highlight ? accentColor : "rgba(21,41,79,0.08)"}`,
                          borderLeft: `4px solid ${accentColor}`,
                          padding: "20px 24px",
                          position: "relative",
                          boxShadow: m.highlight ? "0 4px 16px rgba(21,41,79,0.08)" : "none",
                        }}
                      >
                        <div style={{
                          fontSize: 11,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: accentColor,
                          fontWeight: 700,
                          marginBottom: 6,
                        }}>
                          {m.date}
                        </div>
                        <div style={{
                          fontFamily: "Spectral, serif",
                          fontSize: 20,
                          color: "var(--auprea-navy)",
                          lineHeight: 1.25,
                          marginBottom: 4,
                        }}>
                          {m.title}
                        </div>
                        <div style={{ fontSize: 14, color: "rgba(35,38,46,0.7)", lineHeight: 1.5 }}>
                          {m.place}
                        </div>
                      </div>
                    </div>

                    {/* Icône centrale (desktop) */}
                    <div
                      className="hidden md:flex"
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 52,
                        height: 52,
                        background: "var(--auprea-cream)",
                        border: `2px solid ${accentColor}`,
                        borderRadius: "50%",
                        alignItems: "center",
                        justifyContent: "center",
                        color: accentColor,
                        zIndex: 2,
                      }}
                    >
                      <Icon size={22} />
                    </div>

                    {/* Spacer pour symétrie (vide opposite side) */}
                    <div className="hidden md:block" />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center mt-16">
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.6)", fontStyle: "italic", maxWidth: 600, margin: "0 auto" }}>
              AUPREA poursuit son développement aux côtés des notaires, des conseillers en gestion
              de patrimoine et des familles francophones d&apos;Outre-mer, de métropole et de la
              Caraïbe.
            </p>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="px-6 md:px-12 lg:px-24 py-20" style={{ background: "var(--auprea-navy)", color: "var(--auprea-cream)" }} data-testid="cmm-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 style={{ fontSize: "clamp(2rem, 3.5vw, 2.75rem)", lineHeight: 1.15, color: "var(--auprea-cream)", marginBottom: 16 }}>
            Prêt à anticiper sereinement ?
          </h2>
          <p style={{ fontSize: 17, color: "rgba(243,239,230,0.8)", marginBottom: 32, lineHeight: 1.6 }}>
            Démarrez gratuitement votre carnet AUPREA. Sans engagement, sans carte bancaire.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/inscription" data-testid="cmm-cta-register">
              <button
                style={{
                  padding: "16px 28px",
                  background: "var(--auprea-gold)",
                  color: "var(--auprea-navy)",
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  border: "none",
                  minHeight: 52,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                Créer mon carnet <ArrowRight size={18} />
              </button>
            </Link>
            <Link to="/tarifs" data-testid="cmm-cta-tarifs">
              <button
                style={{
                  padding: "16px 28px",
                  background: "transparent",
                  color: "var(--auprea-cream)",
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  border: "1px solid rgba(243,239,230,0.4)",
                  minHeight: 52,
                }}
              >
                Voir les forfaits
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer minimal */}
      <footer className="px-6 md:px-12 lg:px-24 py-10" style={{ background: "#0c1a36", color: "rgba(243,239,230,0.7)", fontSize: 13 }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between gap-4">
          <div><LogoLight size={20} /></div>
          <div className="flex gap-6">
            <Link to="/" style={{ color: "rgba(243,239,230,0.7)" }}>Accueil</Link>
            <Link to="/tarifs" style={{ color: "rgba(243,239,230,0.7)" }}>Tarifs</Link>
            <Link to="/confidentialite" style={{ color: "rgba(243,239,230,0.7)" }}>Confidentialité</Link>
            <Link to="/mentions-legales" style={{ color: "rgba(243,239,230,0.7)" }}>Mentions légales</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
